/**
 * Admin Auth — усиленная сессионная авторизация
 * - Хранение: sessionStorage (закрытие вкладки = выход)
 * - Токен: HMAC-SHA256 через Web Crypto API
 * - Payload включает: username, iat, exp, userAgent
 * - Срок сессии: 8 часов
 * - При любой ошибке — немедленная очистка токена
 */

const SESSION_KEY = 'admin_session';
const SESSION_HOURS = 8;

// ─── ENV ──────────────────────────────────────────────────────────────────────

export interface AdminEnvResult {
  ok: true;
  username: string;
  password: string;
  secret: string;
}

export interface AdminEnvError {
  ok: false;
  reason: string;
  missing: string[];
}

/**
 * Единственный источник учётных данных.
 * Читает VITE_ADMIN_USERNAME / VITE_ADMIN_PASSWORD / VITE_JWT_SECRET,
 * делает trim(), валидирует наличие всех трёх.
 */
export function getAdminEnv(): AdminEnvResult | AdminEnvError {
  const username = (import.meta.env.VITE_ADMIN_USERNAME ?? '').trim();
  const password = (import.meta.env.VITE_ADMIN_PASSWORD ?? '').trim();
  const secret = (import.meta.env.VITE_JWT_SECRET ?? '').trim();

  const missing: string[] = [];
  if (!username) missing.push('VITE_ADMIN_USERNAME');
  if (!password) missing.push('VITE_ADMIN_PASSWORD');
  if (!secret) missing.push('VITE_JWT_SECRET');

  if (missing.length > 0) {
    return {
      ok: false,
      reason: `Не загружены переменные: ${missing.join(', ')}. Добавьте в .env.local и перезапустите dev-сервер.`,
      missing,
    };
  }

  return { ok: true, username, password, secret };
}

// ─── Session shape ────────────────────────────────────────────────────────────

interface AdminSession {
  token: string;  // HMAC-SHA256 подпись payload
  iat: number;    // issued at (ms)
  exp: number;    // expires at (ms)
}

// ─── Crypto ───────────────────────────────────────────────────────────────────

async function hmacSign(message: string, secret: string): Promise<string> {
  const encoder = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(message));
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function hmacVerify(message: string, signature: string, secret: string): Promise<boolean> {
  try {
    const expected = await hmacSign(message, secret);
    return expected === signature;
  } catch {
    return false;
  }
}

/** Строка payload для подписи: username + iat + exp + userAgent */
function makePayload(username: string, iat: number, exp: number): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : 'server';
  return `${username}|${iat}|${exp}|${ua}`;
}

// ─── Core API ─────────────────────────────────────────────────────────────────

/** Мгновенно удалить битую/просроченную сессию */
function purgeSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch { /* ignore */ }
}

/**
 * Создать новую сессию после успешной проверки логина.
 * Бросает Error при неверных данных или не загруженном ENV.
 */
export async function adminLogin(inputUsername: string, inputPassword: string): Promise<void> {
  const env = getAdminEnv();
  if (!env.ok) {
    throw new Error(env.reason);
  }

  const username = inputUsername.trim();
  const password = inputPassword.trim();

  if (username !== env.username || password !== env.password) {
    throw new Error('Неверный логин или пароль');
  }

  const iat = Date.now();
  const exp = iat + SESSION_HOURS * 60 * 60 * 1000;
  const payload = makePayload(username, iat, exp);
  const token = await hmacSign(payload, env.secret);

  const session: AdminSession = { token, iat, exp };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

/**
 * Выход: удалить сессию.
 */
export function adminLogout(): void {
  purgeSession();
}

/**
 * Полная async проверка: наличие + срок + HMAC-подпись + userAgent.
 * При любой ошибке — очищает sessionStorage и возвращает false.
 */
export async function isAdminAuthed(): Promise<boolean> {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return false;

  const env = getAdminEnv();
  if (!env.ok) return false;

  try {
    const session: AdminSession = JSON.parse(raw);

    // 1. Срок
    if (!session.exp || Date.now() > session.exp) {
      purgeSession();
      return false;
    }

    // 2. Структура
    if (!session.token || !session.iat || !session.exp) {
      purgeSession();
      return false;
    }

    // 3. Нужно знать username из токена — берём из ENV
    const payload = makePayload(env.username, session.iat, session.exp);
    const valid = await hmacVerify(payload, session.token, env.secret);

    if (!valid) {
      purgeSession();
      return false;
    }

    return true;
  } catch {
    purgeSession();
    return false;
  }
}

/**
 * Синхронная быстрая проверка по полю exp (без крипто).
 * Используется только для первого рендера, чтобы не мигать.
 * Полная проверка — isAdminAuthed().
 */
export function isAdminAuthedSync(): boolean {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return false;
  try {
    const session: AdminSession = JSON.parse(raw);
    return typeof session.exp === 'number' && Date.now() < session.exp;
  } catch {
    return false;
  }
}

/**
 * Вернуть оставшееся время сессии в мс. 0 если нет.
 */
export function sessionRemainingMs(): number {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return 0;
  try {
    const session: AdminSession = JSON.parse(raw);
    return Math.max(0, session.exp - Date.now());
  } catch {
    return 0;
  }
}

/**
 * Диагностика для UI (не раскрывает пароль/секрет).
 */
export function getAuthDiagnostics() {
  const env = getAdminEnv();
  const sessionRaw = sessionStorage.getItem(SESSION_KEY);
  let sessionActive = false;
  if (sessionRaw) {
    try {
      const s: AdminSession = JSON.parse(sessionRaw);
      sessionActive = typeof s.exp === 'number' && Date.now() < s.exp;
    } catch { /* ignore */ }
  }

  return {
    envUsernameLoaded: env.ok || !env.missing.includes('VITE_ADMIN_USERNAME'),
    envPasswordLoaded: env.ok || !env.missing.includes('VITE_ADMIN_PASSWORD'),
    envSecretLoaded: env.ok || !env.missing.includes('VITE_JWT_SECRET'),
    webCryptoAvailable: typeof crypto !== 'undefined' && !!crypto.subtle,
    sessionActive,
  };
}
